/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { View, SafeAreaView } from 'react-native';
import { ProductTileViewType } from '@airasia-phoenix/react-native/components/ProductTileView';
import { pd, useAppConfig } from '@airasia-rn/ui';
import { FC, useMemo, useEffect, useRef } from 'react';
import { StyleSheet } from 'react-native-unistyles';
import { ProductTilesViewProps } from '../ProductTilesView/ProductTilesView';
import ProductTileGroupWebView from '../../components/ProductTileGroupWebView';
import { useLogger } from '@airasia-rn/core-logger';
import {
  HomePageEvents,
  trackProductTileClick,
  trackProductTilesViewed,
} from '../../analytics/homepage';

const withImageReloadKey = (iconUrl: string, imageReloadKey?: number) => {
  if (!imageReloadKey) {
    return iconUrl;
  }

  const separator = iconUrl.includes('?') ? '&' : '?';
  return `${iconUrl}${separator}homeImageReloadKey=${imageReloadKey}`;
};

const ProductTilesMobileView: FC<ProductTilesViewProps> = ({
  productTilesData,
  onProductTileClicked,
  imageReloadKey,
}) => {
  const TILES_COLUMNS = 4;

  const { config } = useAppConfig();
  const { logEvent } = useLogger();
  const hasTrackedView = useRef(false);

  const localesWithCustomPrimaryTextLineHeight = ['km-kh', 'th-th'];
  const isCustomPrimaryTextLineHeight =
    localesWithCustomPrimaryTextLineHeight.includes(
      (config.locale ?? '').toLowerCase(),
    );
  const customPrimaryTextStyle = useMemo(
    () =>
      isCustomPrimaryTextLineHeight
        ? pd.comp.product_tile_regular.primaryText.type.lineHeight(24)
        : undefined,
    [isCustomPrimaryTextLineHeight],
  );

  useEffect(() => {
    const tiles = productTilesData?.tiles;
    if (tiles?.length > 0 && !hasTrackedView.current) {
      hasTrackedView.current = true;
      const allTileIds = tiles.map((tile) => tile.productId);
      logEvent(
        HomePageEvents.view,
        trackProductTilesViewed({
          label: allTileIds.join('|'),
        }),
      );
    }
  }, [logEvent, productTilesData?.tiles]);

  const [verticalTiles, horizontalTiles] = useMemo(() => {
    const mappedTiles =
      productTilesData?.tiles?.map((tile, index) => ({
        type: ProductTileViewType.REGULAR,
        iconUrl: withImageReloadKey(tile.iconUrl, imageReloadKey),
        primaryText: tile.title,
        ...(customPrimaryTextStyle
          ? { primaryTextStyle: customPrimaryTextStyle }
          : {}),
        secondaryText: tile.attractText,
        onPress: () => {
          logEvent(
            HomePageEvents.tap,
            trackProductTileClick({
              label: `${tile.productId}|${index + 1}`,
            }),
          );
          if (tile.redirectionUrl) {
            onProductTileClicked(tile.redirectionUrl, tile.productId);
          }
        },
      })) ?? [];

    return [
      mappedTiles.slice(0, TILES_COLUMNS),
      mappedTiles.slice(TILES_COLUMNS).map((tile) => ({
        ...tile,
        type: ProductTileViewType.SMALL,
        primaryTextStyle: isCustomPrimaryTextLineHeight
          ? pd.sys.typescale.caption_text_medium.lineHeight(24)
          : pd.sys.typescale.caption_text_medium,
      })),
    ];
  }, [
    productTilesData?.tiles,
    onProductTileClicked,
    isCustomPrimaryTextLineHeight,
    imageReloadKey,
    customPrimaryTextStyle,
    logEvent,
  ]);

  // Both rows are laid out by the group with CSS units (no measurement),
  // so the tiles are complete in the server HTML and no opacity gate is
  // needed while waiting for onLayout.
  return (
    <SafeAreaView style={styles.container}>
      <View
        style={[
          styles.verticalTiles,
          horizontalTiles.length > 0 && styles.tilesSectionSpacing,
        ]}
      >
        <ProductTileGroupWebView
          key={`vertical-${imageReloadKey ?? 0}`}
          tiles={verticalTiles}
          layout="spread"
        />
      </View>
      {horizontalTiles.length > 0 && (
        <ProductTileGroupWebView
          key={`horizontal-${imageReloadKey ?? 0}`}
          tiles={horizontalTiles}
          layout="scroll"
          testIDPrefix="horizontalTile"
        />
      )}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create((pd) => ({
  container: {
    backgroundColor: pd.sys.colour.container_none.toString(),
  },
  tilesSectionSpacing: {
    marginBottom: pd.ref.space.spacing20.px,
  },
  verticalTiles: {
    paddingHorizontal: pd.ref.space.spacing40.px,
  },
}));

export default ProductTilesMobileView;
